import { Injectable } from '@angular/core';
import { AppConfig } from '../../common/app.config';
import { UvExplorerClient } from '../../common/UvExplorerClient';
import { Observable, Subject } from 'rxjs/Rx';
import { AuditLogSettingsDto } from 'UvExplorerServer.Dtos.TS/Configuration/AuditLogSettingsDto';
import { ServerSettingsDto } from 'UvExplorerServer.Dtos.TS/Configuration/ServerSettingsDto';
import { ValueEncoder } from 'DiscoveryEngine.TS/Utils';
import { GetLinkedServersResultDto } from 'UvExplorerServer.Dtos.TS/Session/GetLinkedServersResultDto';
import { ResultDto, SubServerDto, SuperServerDto } from 'UvExplorerServer.Dtos.TS';
import { ITGlueConnectResultDto, HaloConnectResultDto, HuduConnectResultDto, AssetPandaConnectResultDto, JiraConnectResultDto, SlackConnectResultDto } from 'UvExplorerServer.Dtos.TS/Tools';
import { SmtpSettings } from 'DiscoveryEngine.TS/Smtp';
import { IntegrationTable } from '../../DiscoveryEngine.TS/Core/Integration';

@Injectable()
export class ConfigurationWebService {
	constructor(private appConfig: AppConfig, private uvExplorerClient: UvExplorerClient) {
		return;
	}


	public getDeviceCategories(): Observable<string[]> {
		let url = this.appConfig.webServerUrl + `config/devicecategories`;
		let subject = new Subject<string[]>();

		this.uvExplorerClient.get<string[]>(url).subscribe(
			(result: string[]) => {
				subject.next(result);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public getAuditLogSettings(): Observable<AuditLogSettingsDto> {
		let url = `${this.appConfig.configServiceUrl}api/config/auditlogsettings`
		return this.uvExplorerClient.getObjSer(url, AuditLogSettingsDto);
	}

	public getServerSettings(): Observable<ServerSettingsDto> {
		let url = `${this.appConfig.configServiceUrl}api/config/serversettings`;
		return this.uvExplorerClient.getObjSer(url, ServerSettingsDto);
	}

	public setServerSettings(settings: ServerSettingsDto): Observable<boolean> {
		let context = {};
		let body = {};
		settings.Write(body, context);

		let url = `${this.appConfig.configServiceUrl}api/config/serversettings`;
		return this.uvExplorerClient.putNoResult(url, body);
	}

	public setAuditLogSettings(auditLogSettings: AuditLogSettingsDto): Observable<boolean> {

		let context = {};

		let body = {
			audit_log_settings: {
				log_admin_actions: ValueEncoder.EncodeBoolean(auditLogSettings.log_admin_actions),
				log_login_actions: ValueEncoder.EncodeBoolean(auditLogSettings.log_login_actions),
				log_password_actions: ValueEncoder.EncodeBoolean(auditLogSettings.log_password_actions),
				log_network_change_actions: ValueEncoder.EncodeBoolean(auditLogSettings.log_network_change_actions)
			}
		};

		let url = this.appConfig.configServiceUrl + `api/config/auditlogsettings`;
		return this.uvExplorerClient.putNoResult(url, body);
	}

	public getLinkedServers(): Observable<GetLinkedServersResultDto> {
		let url = `${this.appConfig.sessionServiceUrl}api/session/linkedservers`
		return this.uvExplorerClient.getObjSer(url, GetLinkedServersResultDto);
	}

	public linkSuperServer(superServer: SuperServerDto): Observable<boolean> {
		let context = {};	
		let body = {
			link_super_server_request: {}
		};
		superServer.Write(body.link_super_server_request, context);

		let url = `${this.appConfig.configServiceUrl}api/session/superserver/link`
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public unlinkSuperServer(): Observable<boolean> {
		let url = `${this.appConfig.configServiceUrl}api/session/superserver/unlink`
		return this.uvExplorerClient.postNoResult(url, "");
	}

	public syncWithSuperServer(): Observable<boolean> {
		let url = `${this.appConfig.configServiceUrl}api/session/superserver/sync`
		return this.uvExplorerClient.postNoResult(url, "");
	}

	public unlinkSubServer(subServer: SubServerDto): Observable<boolean> {
		let body = {
			unlink_sub_server_request: {
				sub_server_guid: subServer.SubServerGuid,
				sub_tenant_guid: subServer.SubTenantGuid,
			}
		};

		let url = `${this.appConfig.configServiceUrl}api/session/subserver/unlink`
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public testEmail(smtpSettings: SmtpSettings): Observable<ResultDto> {
		let context = {};	
		let body = {
			test_email_request: {
			}
		};
		smtpSettings.Write(body.test_email_request, context);

		let url = `${this.appConfig.configServiceUrl}api/config/email/test`
		return this.uvExplorerClient.postObjSer(url, body, ResultDto);
	}

	public getIntegrationTable(): Observable<IntegrationTable> {
		let url = this.appConfig.webServerUrl + `api/config/integrations`;
		return this.uvExplorerClient.getObjSer(url, IntegrationTable);
	}

	public setIntegrationTable(integrationSettings: IntegrationTable): Observable<boolean> {
		let context = {};

		let body = {};

		if (integrationSettings) {
			integrationSettings.Write(body, context);
		}

		let url = this.appConfig.configServiceUrl + `api/config/integrations`;
		return this.uvExplorerClient.putNoResult(url, body);
	}

	public testITGlueConnect(apiURL: string, apiKey: string): Observable<ITGlueConnectResultDto> {

		let url = this.appConfig.webServerUrl + `api/session/itglueconnect`;
		let subject = new Subject<ITGlueConnectResultDto>();

		let body = {
			itglue_connect_request: {
				target_server_api_url: apiURL,
				target_server_api_key: apiKey,
			}
		};

		this.uvExplorerClient.post<ITGlueConnectResultDto>(url, body).subscribe(
			(result: ITGlueConnectResultDto) => {
				subject.next(result);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public testHaloConnect(apiURL: string, apiClientID: string, apiUsername: string, apiPassword: string): Observable<HaloConnectResultDto> {

		let url = this.appConfig.webServerUrl + `api/session/haloconnect`;
		let subject = new Subject<HaloConnectResultDto>();

		let body = {
			halo_connect_request: {
				target_server_api_url: apiURL,
				target_server_client_id: apiClientID,
				target_server_username: apiUsername,
				target_server_password: apiPassword,
			}
		};

		this.uvExplorerClient.post<HaloConnectResultDto>(url, body).subscribe(
			(result: HaloConnectResultDto) => {
				subject.next(result);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public testHuduConnect(apiURL: string, apiKey: string): Observable<HuduConnectResultDto> {

		let url = this.appConfig.webServerUrl + `api/session/huduconnect`;
		let subject = new Subject<HuduConnectResultDto>();

		let body = {
			hudu_connect_request: {
				target_server_api_url: apiURL,
				target_server_api_key: apiKey,
			}
		};

		this.uvExplorerClient.post<HuduConnectResultDto>(url, body).subscribe(
			(result: HuduConnectResultDto) => {
				subject.next(result);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public testAssetPandaConnect(serverURL: string, username: string, password: string): Observable<AssetPandaConnectResultDto> {

		let url = this.appConfig.webServerUrl + `api/session/assetpandaconnect`;
		let subject = new Subject<AssetPandaConnectResultDto>();

		let body = {
			assetpanda_connect_request: {
				server_url: serverURL,
				user_name: username,
				password: password,
			}
		};

		this.uvExplorerClient.post<AssetPandaConnectResultDto>(url, body).subscribe(
			(result: AssetPandaConnectResultDto) => {
				subject.next(result);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public testJiraConnect(serverURL: string, username: string, apiToken: string): Observable<JiraConnectResultDto> {

		let url = this.appConfig.webServerUrl + `api/session/jiraconnect`;
		let subject = new Subject<JiraConnectResultDto>();

		let body = {
			jira_connect_request: {
				server_url: serverURL,
				user_name: username,
				api_token: apiToken,
			}
		};

		this.uvExplorerClient.post<JiraConnectResultDto>(url, body).subscribe(
			(result: JiraConnectResultDto) => {
				subject.next(result);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public testSlackConnect(webhooks: string): Observable<SlackConnectResultDto> {

		let url = this.appConfig.webServerUrl + `api/session/slackconnect`;
		let subject = new Subject<SlackConnectResultDto>();

		let body = {
			slack_connect_request: {
				webhooks: webhooks
			}
		};

		this.uvExplorerClient.post<SlackConnectResultDto>(url, body).subscribe(
			(result: SlackConnectResultDto) => {
				subject.next(result);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}
}